import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  viewChild,
  ViewChild,
} from '@angular/core';
// app
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CommunitySpaceDropdownComponent } from '@components/master-layout/navs/components/community-space-dropdown/community-space-dropdown.component';
import { LanguagesComponent } from '@components/master-layout/navs/components/languages/languages.component';
import { NavItemDropdownComponent } from '@components/master-layout/navs/components/nav-item-dropdown/nav-item-dropdown.component';
import { NavItemComponent } from '@components/master-layout/navs/components/nav-item/nav-item.component';
import { NavNewsMessagesComponent } from '@components/master-layout/navs/components/nav-news-messages/nav-news-messages.component';
import { NavProfileComponent } from '@components/master-layout/navs/components/nav-profile/nav-profile.component';
import { navs } from '@configs/index';
import { AuthenService } from '@core/authen/authen.service';
import { SubdomainOrgFilterMarker } from '@core/bootstrap/subdomain-org-filter.initializer';
import { I18nService } from '@core/i18n/i18n.service';
import { CentralConfigService } from '@core/services/central-config.service';
import { CommunitySelectorStoredService } from '@core/services/community-selector-stored.service';
import { SearchEntitiesService } from '@core/services/search-entities.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import {
  NgbDropdownModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { matAccountCircle } from '@ng-icons/material-icons/baseline';
import {
  matArrowBackOutline,
  matArrowDropDownOutline,
  matCloseOutline,
  matMenuOutline,
  matSearchOutline,
} from '@ng-icons/material-icons/outline';
import { TranslateModule } from '@ngx-translate/core';
import { BaseComponentWithServiceComponent } from '@shared/components/base-list/base-component-with-service/base-component-with-service.component';
import { GlobalFilterComponent } from '@shared/components/global-filter/global-filter.component';
import { InternalIconComponent } from '@shared/components/internal-icon/internal-icon.component';
import { SharedIconComponent } from '@shared/components/shared-icon/shared-icon.component';
import { DebounceClickDirective } from '@shared/directives/debounce-click.directive';
import { DefaultPlaceholderDirective } from '@shared/directives/default-placeholder.directive';
import { DynamicImgUrlDirective } from '@shared/directives/dynamic-img-url.directive';
import { UnsavedFormCheckDirective } from '@shared/directives/unsaved-form-check.directive';
import { UrlParam } from '@shared/enums/url-param.enum';
import { untilDestroyed } from '@shared/functions/until-destroyed';
import { NavInterface } from '@shared/interfaces/nav.interface';
import { OrganizationFilter } from '@shared/interfaces/organization.interface';
import { UserInterface } from '@shared/interfaces/user.interface';
import { FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';
import { Event as EventBusEvent } from 'ngx-eventbus/lib/service/event';

@Component({
  selector: 'app-navs',
  templateUrl: './navs.component.html',
  imports: [
    NgIcon,
    CommonModule,
    UnsavedFormCheckDirective,
    InternalIconComponent,
    DefaultPlaceholderDirective,
    DynamicImgUrlDirective,
    TranslateModule,
    FormsModule,
    NavItemComponent,
    NgbDropdownModule,
    NgbTooltipModule,
    NavItemDropdownComponent,
    NavNewsMessagesComponent,
    NavProfileComponent,
    LanguagesComponent,
    SharedIconComponent,
    CommunitySpaceDropdownComponent,
    DebounceClickDirective,
  ],
  viewProviders: [
    provideIcons({
      matAccountCircle,
      matArrowBackOutline,
      matArrowDropDownOutline,
      matCloseOutline,
      matMenuOutline,
      matSearchOutline,
    }),
  ],
})
export class NavsComponent
  extends BaseComponentWithServiceComponent
  implements OnInit
{
  @Input() global_org_id: string; // Central config

  navs: NavInterface[] = [];
  isNavsActivated = false;
  isSearchActivated = false;
  globalFilterRegisterEvent: EventBusEvent;
  isVisibleFilterButton = false;
  orgUrl = environment.jipUrl.organizations;

  profile: UserInterface;
  isShowSearchBar = false;
  searchText = '';

  homeNav = { route: '/', name: 'Home', jipIcon: 'home' };

  @ViewChild('globalFilter') globalFilter: GlobalFilterComponent;
  navDropdownItems: NavInterface[] = [];

  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  selectedOrg: OrganizationFilter;

  private readonly centralConfig = inject(CentralConfigService);

  // Below md the header renders as the mobile drawer.
  mobileHeader = false;

  // True between the mobile drawer and XL: every nav item moves into the
  // More dropdown.
  private navsCollapsed = false;

  get tenantLogo(): string {
    return this.sessionService.getTenantLogo();
  }

  get filterTokenIdParam() {
    return StringUtils.getParamFromUrl(UrlParam.FilterTokenId);
  }

  get communitySpacesDropdownDisplayed() {
    return !this.centralConfig.innovationSpaceId;
  }

  get activeInnovationSpaceLogoDisplayed(): boolean {
    const imgUrl = this.selectedOrg?.imgUrl ?? this.selectedOrg?.logo?.url;
    const hasActiveFilterToken = Boolean(
      this.filterTokenIdParam || SubdomainOrgFilterMarker.lastAppliedSubdomain
    );

    return Boolean(
      (imgUrl && hasActiveFilterToken) || (!imgUrl && !this.tenantLogo)
    );
  }

  constructor(
    readonly sessionService: SessionService,
    readonly authenService: AuthenService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly i18nService: I18nService,
    readonly communitySelectorStored: CommunitySelectorStoredService,
    readonly searchEntitiesService: SearchEntitiesService
  ) {
    super(sessionService);

    this.listenToViewportChange();
    this.listenToAuthChange();
  }

  ngOnInit(): void {
    this.sessionService.isHeaderVisible$.next(true);

    const globalSearchParam = StringUtils.getParamFromUrl(
      UrlParam.GlobalSearchTab
    );

    if (globalSearchParam && !this.mobileHeader) {
      this.isShowSearchBar = true;
    }

    this.communitySelectorStored.selectedOrg
      .pipe(untilDestroyed(this))
      .subscribe((org: OrganizationFilter) => {
        this.selectedOrg = org;
      });

    this.communitySelectorStored.keyword
      .pipe(untilDestroyed(this))
      .subscribe((keyword: string) => {
        this.searchText = keyword;
      });
  }

  loadProfile(): void {
    this.authenService.getProfile().subscribe(profile => {
      this.profile = profile;
    });
  }

  toggleNavs(): void {
    this.isNavsActivated = !this.isNavsActivated;
  }

  toggleSearch(): void {
    this.isSearchActivated = !this.isSearchActivated;
  }

  toggleSearchBar(): void {
    this.isShowSearchBar = !this.isShowSearchBar;

    setTimeout(() => {
      this.searchInput()?.nativeElement?.focus();
    });
  }

  navigateToActiveInnovationSpaceDetail(org?: OrganizationFilter) {
    if (org?.slug || org?.id) {
      const url = org.isChallenge ? environment.jipUrl.challenges : this.orgUrl;

      FormUtils.navigateToWithLanguagePath(
        this.sessionService,
        url + '/' + (org.slug || org.id)
      );
    } else {
      FormUtils.navigateToWithLanguagePath(this.sessionService, '/');
    }
  }

  onSearch() {
    this.communitySelectorStored.search();
  }

  onClearText() {
    this.communitySelectorStored.setKeyword('');
    this.communitySelectorStored.search();
    this.searchInput()?.nativeElement?.focus();
  }

  onSearchTextChange(event) {
    this.communitySelectorStored.setKeyword(event);
  }

  private listenToViewportChange() {
    const BREAKPOINTS = {
      // Below md the header switches to the mobile drawer, which lists
      // every nav item instead of collapsing them into the More dropdown.
      MOBILE: '(max-width: 767.98px)',

      XL: '(min-width: 1200px)',
    };

    this.breakpointObserver
      .observe(Object.values(BREAKPOINTS))
      .subscribe((state: BreakpointState) => {
        const { breakpoints } = state;

        this.mobileHeader = breakpoints[BREAKPOINTS.MOBILE];

        // The inline search bar only renders on md and up, and its close
        // button lives inside it. Leaving the flag on below md would hide the
        // nav items with no way to bring them back.
        if (this.mobileHeader) {
          this.isShowSearchBar = false;
        }

        this.navsCollapsed = !(
          this.mobileHeader || breakpoints[BREAKPOINTS.XL]
        );

        this.applyNavItems();
      });
  }

  private listenToAuthChange(): void {
    this.authenService.authenticated().subscribe(() => {
      this.applyNavItems();
    });
  }

  private applyNavItems(): void {
    const navItems = [...navs];

    if (!this.sessionService.authenticated) {
      navItems.push({
        route: environment.jipUrl.clientLandingPage,
        name: 'UI.Common.AboutUs',
        ngIcon: 'matInfo',
      });
    }

    if (this.navsCollapsed) {
      this.navs = [];
      this.navDropdownItems = navItems;
    } else {
      this.navs = navItems;
      this.navDropdownItems = [];
    }
  }

  shouldActiveMore(): boolean {
    const currentUri = location.origin + location.pathname;
    const locale = this.i18nService.language?.locale.toLowerCase();

    return this.navDropdownItems.some(({ route }) => {
      const itemRoute = location.origin + '/' + locale + route;
      return (
        currentUri.includes(itemRoute) &&
        currentUri.length - itemRoute.length < 2
      );
    });
  }

  closeFilters(): void {
    this.isSearchActivated = false;
  }

  onHomeIconClick(): void {
    FormUtils.navigateToWithLanguagePath(
      this.sessionService,
      environment.jipUrl.startPage
    );
  }

  goToLoginPage(): void {
    const url = environment.jipUrl.login;

    this.sessionService.setLoginCallBackpage(location.href);
    FormUtils.navigateTo(this.sessionService.appendLanguagePath(url));
  }
}
